import { t } from '../i18n';
import { useState } from 'react';

import type { Property, PropertyOption, PropertyType } from '@noti/shared';

import {
  ADDABLE_PROPERTY_TYPES,
  CHIP_COLOR,
  DEFAULT_OPTIONS,
  OPTION_COLOR_CYCLE,
  PTYPE_ICON,
  TYPE_LABEL,
} from './database-rows';
import { Dropdown, type DropdownOption } from './DatabaseDropdown';
import { Glyph } from './icons';
import { OptionEditor } from './OptionEditor';

/**
 * The database's Properties panel (`c.html`, props panel). Every change is
 * written straight to the shared schema; there is no draft for the schema
 * itself.
 */

export interface DatabasePropertiesProps {
  properties: Property[];
  hidden: string[];
  onChange: (properties: Property[]) => void;
  onToggleVisible: (propertyId: string, shown: boolean) => void;
}

export function DatabaseProperties({
  properties,
  hidden,
  onChange,
  onToggleVisible,
}: DatabasePropertiesProps): React.ReactElement {
  const [openOptions, setOpenOptions] = useState<string | null>(null);
  const [editingOption, setEditingOption] = useState<string | null>(null);
  const [optionDraft, setOptionDraft] = useState('');
  const [newOption, setNewOption] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<PropertyType>('text');
  const [warning, setWarning] = useState<string | null>(null);

  const typeOptions: DropdownOption[] = ADDABLE_PROPERTY_TYPES.map((type) => ({
    value: type,
    label: TYPE_LABEL[type],
    icon: PTYPE_ICON[type],
  }));

  const patch = (id: string, change: Partial<Property>) =>
    onChange(
      properties.map((property) => (property.id === id ? { ...property, ...change } : property)),
    );

  const addProperty = () => {
    const name = newName.trim();
    if (!name) {
      setWarning(t('database.propertyNeedsName'));
      return;
    }
    setWarning(null);
    onChange([
      ...properties,
      {
        id: crypto.randomUUID(),
        name,
        type: newType,
        options: DEFAULT_OPTIONS[newType] ? [...DEFAULT_OPTIONS[newType]] : [],
      },
    ]);
    setNewName('');
  };

  const addOption = (property: Property, raw: string) => {
    const name = raw.trim();
    // An existing name is left alone rather than silently added twice.
    if (!name || property.options.some((option) => option.n === name)) return;
    const color = OPTION_COLOR_CYCLE[property.options.length % OPTION_COLOR_CYCLE.length];
    if (!color) return;
    patch(property.id, { options: [...property.options, { n: name, c: color }] });
    // Only clear on success: a rejected name stays visible instead of vanishing.
    setNewOption('');
  };

  const replaceOption = (property: Property, name: string, change: Partial<PropertyOption>) => {
    patch(property.id, {
      options: property.options.map((option) =>
        option.n === name ? { ...option, ...change } : option,
      ),
    });
  };

  return (
    <div className="dbprops">
      <div className="dbprops__list">
        {properties.map((property) => {
          const shown = property.type === 'title' || !hidden.includes(property.id);
          const hasOptions =
            property.type === 'select' || property.type === 'status' || property.type === 'multi';
          return (
            <div className="dbprops__group" key={property.id}>
              <div className="prw">
                <span className="dbprops__icon" aria-hidden="true">
                  <Glyph d={PTYPE_ICON[property.type]} size={14} strokeWidth={1.9} />
                </span>
                <input
                  className="ed dbprops__name"
                  value={property.name}
                  aria-label={t('database.propertyName')}
                  onChange={(event) => patch(property.id, { name: event.target.value })}
                />
                <button
                  type="button"
                  className="dbprops__type"
                  aria-expanded={openOptions === property.id}
                  aria-label={
                    hasOptions
                      ? t('database.editOptionsFor', { name: property.name })
                      : t('database.typeOf', { label: TYPE_LABEL[property.type] })
                  }
                  onClick={() => {
                    if (!hasOptions) return;
                    setOpenOptions(openOptions === property.id ? null : property.id);
                  }}
                >
                  {TYPE_LABEL[property.type]}
                </button>
                <button
                  type="button"
                  role="switch"
                  aria-checked={shown}
                  aria-label={
                    shown
                      ? t('database.hideProperty', { name: property.name })
                      : t('database.showProperty', { name: property.name })
                  }
                  className={`dbprops__eye${shown ? ' is-on' : ''}`}
                  onClick={() => {
                    if (property.type === 'title') return;
                    onToggleVisible(property.id, shown);
                  }}
                >
                  <span className="dbprops__knob" />
                </button>
                {property.type === 'title' ? null : (
                  <button
                    type="button"
                    className="dbprops__delete"
                    aria-label={t('database.deleteProperty')}
                    title={t('database.deleteProperty')}
                    onClick={() => onChange(properties.filter((item) => item.id !== property.id))}
                  >
                    ×
                  </button>
                )}
              </div>
              {openOptions === property.id && hasOptions ? (
                <div className="dbprops__options">
                  {property.options.length === 0 ? (
                    <span className="dbprops__empty">{t('database.noOptions')}</span>
                  ) : null}
                  {property.options.map((option) => (
                    <div className="dbprops__option" key={option.n}>
                      <div className="dbprops__optionrow">
                        <span
                          className="chip"
                          style={{
                            background: CHIP_COLOR[option.c].bg,
                            color: CHIP_COLOR[option.c].fg,
                          }}
                        >
                          {option.n}
                        </span>
                        <button
                          type="button"
                          className="dbprops__optedit"
                          aria-label={t('database.editOption', { name: option.n })}
                          aria-expanded={editingOption === option.n}
                          onClick={() => {
                            setEditingOption(editingOption === option.n ? null : option.n);
                            setOptionDraft(option.n);
                          }}
                        >
                          <Glyph
                            d={['M5 12h.01', 'M12 12h.01', 'M19 12h.01']}
                            size={16}
                            strokeWidth={3}
                          />
                        </button>
                      </div>
                      {editingOption === option.n ? (
                        <OptionEditor
                          name={optionDraft}
                          color={option.c}
                          onName={setOptionDraft}
                          onSave={() => {
                            replaceOption(property, option.n, {
                              n: optionDraft.trim() || option.n,
                            });
                            setEditingOption(null);
                          }}
                          onRecolor={(color) => replaceOption(property, option.n, { c: color })}
                          onDelete={() => {
                            patch(property.id, {
                              options: property.options.filter((item) => item.n !== option.n),
                            });
                            setEditingOption(null);
                          }}
                        />
                      ) : null}
                    </div>
                  ))}
                  <input
                    className="ni dbprops__newoption"
                    value={newOption}
                    placeholder={t('database.newOption')}
                    aria-label={t('database.newOption')}
                    onChange={(event) => setNewOption(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter') return;
                      addOption(property, newOption);
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="dbprops__new">
        <span className="dbprops__newicon" aria-hidden="true">
          +
        </span>
        <input
          className="ni dbprops__newinput"
          value={newName}
          placeholder={t('database.newPropertyPlaceholder')}
          aria-label={t('database.newPropertyName')}
          onChange={(event) => {
            setNewName(event.target.value);
            setWarning(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') addProperty();
          }}
        />
        <Dropdown
          className="dbprops__typedd"
          label={t('database.type')}
          value={newType}
          options={typeOptions}
          onPick={(value) => setNewType(value as PropertyType)}
        />
      </div>
      {warning ? <span className="dbprops__warning">{warning}</span> : null}
    </div>
  );
}
